'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Chip, type ChipValue } from '../../../Chip/Chip';
import { FilterSectionTitle } from '../FilterSectionTitle/FilterSectionTitle';

type FilterWithChipsProps<T extends string> = {
  onValueChange: (value: T[]) => void;
  namespace: string;
  chips: ChipValue<T>[];
  hasTooltip?: boolean;
  className?: string;
  value?: T[];
};
function FilterWithChips<T extends string = string>({ value, onValueChange, namespace, chips, hasTooltip = false, className, ...props }: FilterWithChipsProps<T>) {
  const t = useTranslations(namespace);
  const [selectedChips, setSelectedChips] = useState<T[]>(value || []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedChips(value || []);
  }, [value]);

  const handleChipClick = ({ value, checked }: { value: T; checked: boolean }) => {
    let updatedSelectedChips = [];
    if (checked) {
      updatedSelectedChips = [...selectedChips, value];
    } else {
      updatedSelectedChips = selectedChips.filter(chipValue => chipValue !== value);
    }
    setSelectedChips(updatedSelectedChips);
    onValueChange(updatedSelectedChips);
  };

  return (
    <div className={className} data-testid="filter-container" {...props}>
      <FilterSectionTitle
        title={t('title')}
        tooltipContent={hasTooltip ? t('tooltip') : undefined}
        currentItems={selectedChips.length}
        totalItems={chips.length}
        data-track-name="info"
      />
      <div className="flex flex-wrap gap-2 pt-4">
        {chips.map(chip => (
          <Chip
            key={chip.value}
            label={chip.label}
            value={chip.value}
            onChange={handleChipClick}
            isChecked={selectedChips.includes(chip.value)}
            data-testid={`filter-chip-${chip.value.toLowerCase().replace(/\s+/g, '-')}`}
            data-track-name={chip.label}
            data-track-selected={selectedChips.includes(chip.value) ? 'Yes' : 'No'}
          />
        ))}
      </div>
    </div>
  );
}

export { FilterWithChips, type FilterWithChipsProps };
